import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { checkInService, MilestoneRequest } from '@/services/checkin.service';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

interface MilestoneFormModalProps {
  open: boolean;
  onClose: () => void;
  milestone?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export function MilestoneFormModal({
  open,
  onClose,
  milestone,
}: MilestoneFormModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<MilestoneRequest>({
    milestoneName: '',
    dayCount: 1,
    tokenAmount: 0,
    bonusMultiplier: null,
    description: '',
    iconUrl: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when editing
  useEffect(() => {
    if (milestone) {
      setFormData({
        milestoneName: milestone.milestoneName,
        dayCount: milestone.dayCount,
        tokenAmount: milestone.tokenAmount,
        bonusMultiplier: milestone.bonusMultiplier,
        description: milestone.description || '',
        iconUrl: milestone.iconUrl || '',
        isActive: milestone.isActive,
      });
    } else {
      setFormData({
        milestoneName: '',
        dayCount: 1,
        tokenAmount: 0,
        bonusMultiplier: null,
        description: '',
        iconUrl: '',
        isActive: true,
      });
    }
    setErrors({});
  }, [milestone, open]);

  const saveMutation = useMutation({
    mutationFn: (data: MilestoneRequest) =>
      milestone
        ? checkInService.updateMilestone(milestone.id, data)
        : checkInService.createMilestone(data),
    onSuccess: () => {
      toast.success(
        `Milestone ${milestone ? 'updated' : 'created'} successfully`,
      );
      queryClient.invalidateQueries({ queryKey: ['checkin-milestones'] });
      handleClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to save milestone';
      toast.error(message);
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.milestoneName.trim()) {
      newErrors.milestoneName = 'Milestone name is required';
    }

    if (formData.dayCount <= 0) {
      newErrors.dayCount = 'Day count must be greater than 0';
    }

    if (formData.tokenAmount < 0) {
      newErrors.tokenAmount = 'Token amount cannot be negative';
    }

    if (formData.bonusMultiplier !== null && formData.bonusMultiplier < 0) {
      newErrors.bonusMultiplier = 'Bonus multiplier cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    saveMutation.mutate(formData);
  };

  const handleClose = () => {
    setFormData({
      milestoneName: '',
      dayCount: 1,
      tokenAmount: 0,
      bonusMultiplier: null,
      description: '',
      iconUrl: '',
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {milestone ? 'Edit Milestone' : 'Create Milestone'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Milestone Name */}
          <div className="space-y-2">
            <Label htmlFor="milestoneName">
              Milestone Name <span className="text-danger">*</span>
            </Label>
            <Input
              id="milestoneName"
              value={formData.milestoneName}
              onChange={(e) =>
                setFormData({ ...formData, milestoneName: e.target.value })
              }
              placeholder="e.g., 7 Day Streak"
            />
            {errors.milestoneName && (
              <p className="text-sm text-danger">{errors.milestoneName}</p>
            )}
          </div>

          {/* Day Count and Token Amount */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dayCount">
                Day Count <span className="text-danger">*</span>
              </Label>
              <Input
                id="dayCount"
                type="number"
                min="1"
                value={formData.dayCount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dayCount: parseInt(e.target.value) || 1,
                  })
                }
              />
              {errors.dayCount && (
                <p className="text-sm text-danger">{errors.dayCount}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tokenAmount">
                Token Amount <span className="text-danger">*</span>
              </Label>
              <Input
                id="tokenAmount"
                type="number"
                min="0"
                value={formData.tokenAmount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tokenAmount: parseInt(e.target.value) || 0,
                  })
                }
              />
              {errors.tokenAmount && (
                <p className="text-sm text-danger">{errors.tokenAmount}</p>
              )}
            </div>
          </div>

          {/* Bonus Multiplier */}
          <div className="space-y-2">
            <Label htmlFor="bonusMultiplier">Bonus Multiplier (optional)</Label>
            <Input
              id="bonusMultiplier"
              type="number"
              min="0"
              step="0.1"
              value={formData.bonusMultiplier ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  bonusMultiplier: e.target.value
                    ? parseFloat(e.target.value)
                    : null,
                })
              }
              placeholder="e.g., 1.5"
            />
            {errors.bonusMultiplier && (
              <p className="text-sm text-danger">{errors.bonusMultiplier}</p>
            )}
            <p className="text-sm text-muted-foreground">
              Multiplier applied to bonus rewards (leave empty for none)
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Describe this milestone..."
              rows={3}
            />
          </div>

          {/* Icon URL */}
          <div className="space-y-2">
            <Label htmlFor="iconUrl">Icon URL (optional)</Label>
            <Input
              id="iconUrl"
              value={formData.iconUrl}
              onChange={(e) =>
                setFormData({ ...formData, iconUrl: e.target.value })
              }
              placeholder="https://example.com/icon.png"
            />
          </div>

          {/* Is Active */}
          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isActive: checked })
              }
            />
            <Label htmlFor="isActive" className="cursor-pointer">
              Active (users can achieve this milestone)
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saveMutation.isPending}>
            {saveMutation.isPending
              ? 'Saving...'
              : milestone
                ? 'Update'
                : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
